import React, { useState, useEffect, useCallback } from 'react';
import {
  Briefcase, MapPin, Clock, ExternalLink, Plus, X, Loader2,
  CheckCircle, AlertCircle, Mail, Phone, Pencil, Trash2, LogIn
} from 'lucide-react';
import { BackButton } from './shared';
import { educationJobs } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { PARISHES } from '../lib/schoolDirectory';

// The jobs board.
//
// The table, the read path and this page all existed; what did not exist was
// any way to get a row in. `education_jobs` had exactly two policies — public
// SELECT and admin ALL — so `educationJobs.create()` failed for every ordinary
// account and the board has shown "No Job Listings Yet" since launch.
//
// Posting now works for signed-in users and goes through review:
//   submit -> pending -> an admin approves or rejects -> live on the board
//
// Status and ownership are stamped by a database trigger, not by this file.
// See supabase/migration-education-job-postings.sql.

const JOB_TYPES = [
  { value: 'full_time', label: 'Full time' },
  { value: 'part_time', label: 'Part time' },
  { value: 'contract', label: 'Contract' },
  { value: 'temporary', label: 'Temporary' },
];

const ROLE_TYPES = [
  { value: 'teacher', label: 'Teaching' },
  { value: 'administrator', label: 'Administration' },
  { value: 'support', label: 'Student support' },
  { value: 'auxiliary', label: 'Auxiliary' },
  { value: 'other', label: 'Other' },
];

const labelFor = (list, value) => list.find(o => o.value === value)?.label || value;

const EMPTY_FORM = {
  title: '',
  employer_name: '',
  parish: '',
  role_type: 'teacher',
  job_type: 'full_time',
  salary_range: '',
  description: '',
  requirements: '',
  apply_url: '',
  contact_email: '',
  contact_phone: '',
};

const STATUS_BADGE = {
  pending: { cls: 'bg-amber-100 text-amber-800 ring-amber-200', label: 'Awaiting review' },
  approved: { cls: 'bg-brand-100 text-brand-800 ring-brand-200', label: 'Live' },
  rejected: { cls: 'bg-red-100 text-red-800 ring-red-200', label: 'Not published' },
};

const Field = ({ label, hint, children, required }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-ink mb-1.5">
      {label}
      {required && <span className="text-red-600 ml-0.5" aria-hidden="true">*</span>}
    </span>
    {children}
    {hint && <span className="block text-xs text-ink-muted mt-1">{hint}</span>}
  </label>
);

const inputCls =
  'w-full px-3 py-2.5 rounded-xl bg-canvas-raised ring-1 ring-hairline text-[15px] text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500';

const JobCard = ({ job, footer }) => (
  <article className="bg-canvas-raised rounded-2xl p-5 ring-1 ring-hairline shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h3 className="font-bold text-ink text-[17px] leading-tight">{job.title}</h3>
        {(job.employer_name || job.school?.name) && (
          <p className="text-sm text-ink-muted mt-0.5">{job.employer_name || job.school?.name}</p>
        )}
      </div>
      {job.status && STATUS_BADGE[job.status] && footer && (
        <span
          className={`flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ring-1 ${STATUS_BADGE[job.status].cls}`}
        >
          {STATUS_BADGE[job.status].label}
        </span>
      )}
    </div>

    <div className="flex flex-wrap gap-2 mt-3">
      {job.parish && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted bg-canvas px-2.5 py-1 rounded-full ring-1 ring-hairline">
          <MapPin className="w-3 h-3" aria-hidden="true" /> {job.parish}
        </span>
      )}
      {job.job_type && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-800 bg-brand-50 px-2.5 py-1 rounded-full ring-1 ring-brand-100">
          <Clock className="w-3 h-3" aria-hidden="true" /> {labelFor(JOB_TYPES, job.job_type)}
        </span>
      )}
      {job.role_type && (
        <span className="text-xs font-medium text-brand-800 bg-brand-50 px-2.5 py-1 rounded-full ring-1 ring-brand-100">
          {labelFor(ROLE_TYPES, job.role_type)}
        </span>
      )}
      {job.salary_range && (
        <span className="text-xs font-medium text-ink-muted bg-canvas px-2.5 py-1 rounded-full ring-1 ring-hairline">
          {job.salary_range}
        </span>
      )}
    </div>

    {job.description && (
      <p className="text-[15px] text-ink-muted leading-relaxed mt-3 whitespace-pre-line">{job.description}</p>
    )}
    {job.requirements && (
      <p className="text-sm text-ink-muted leading-relaxed mt-2 whitespace-pre-line">
        <span className="font-semibold text-ink">Requirements: </span>
        {job.requirements}
      </p>
    )}

    <div className="flex flex-wrap items-center gap-4 mt-4">
      {job.apply_url && (
        <a
          href={job.apply_url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-900 hover:underline"
        >
          Apply <ExternalLink className="w-4 h-4" aria-hidden="true" />
        </a>
      )}
      {job.contact_email && (
        <a
          href={`mailto:${job.contact_email}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-900 hover:underline"
        >
          <Mail className="w-4 h-4" aria-hidden="true" /> {job.contact_email}
        </a>
      )}
      {job.contact_phone && (
        <a
          href={`tel:${job.contact_phone.replace(/[^\d+]/g, '')}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-900 hover:underline"
        >
          <Phone className="w-4 h-4" aria-hidden="true" /> {job.contact_phone}
        </a>
      )}
    </div>

    {footer}
  </article>
);

export const EducationJobsPage = ({ setPage, goBack }) => {
  const { user, isAuthenticated } = useAuth();

  const [jobsList, setJobsList] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ parish: '', role_type: '', job_type: '' });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');

  const loadJobs = useCallback(async () => {
    setLoading(true);
    const { data } = await educationJobs.getAll(filters);
    setJobsList(data || []);
    setLoading(false);
  }, [filters]);

  const loadMine = useCallback(async () => {
    if (!user?.id) return setMine([]);
    const { data } = await educationJobs.getMine(user.id);
    return setMine(data || []);
  }, [user?.id]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const startPost = () => {
    if (!isAuthenticated) return setPage('login');
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormError('');
    setShowForm(true);
  };

  const startEdit = (job) => {
    setForm({ ...EMPTY_FORM, ...Object.fromEntries(
      Object.keys(EMPTY_FORM).map(k => [k, job[k] ?? ''])
    ) });
    setEditingId(job.id);
    setFormError('');
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.title.trim()) return setFormError('Give the position a title.');
    if (!form.employer_name.trim()) return setFormError('Say which school or organisation is hiring.');
    if (!form.apply_url.trim() && !form.contact_email.trim() && !form.contact_phone.trim()) {
      return setFormError('Add at least one way to apply — a link, an e-mail address or a phone number.');
    }

    setSaving(true);
    const payload = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === 'string' ? v.trim() || null : v])
    );
    const { error } = editingId
      ? await educationJobs.update(editingId, payload)
      : await educationJobs.create(payload, user.id);
    setSaving(false);

    if (error) {
      setFormError(error.message || 'Could not save the listing. Please try again.');
      return;
    }

    setShowForm(false);
    setForm(EMPTY_FORM);
    setEditingId(null);
    setNotice(
      editingId
        ? 'Listing updated. Edits go back for review before they reappear on the board.'
        : 'Listing submitted. It goes live once an administrator approves it.'
    );
    loadMine();
    loadJobs();
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    await educationJobs.delete(job.id);
    setNotice('Listing deleted.');
    loadMine();
    loadJobs();
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div className="w-full">
      <BackButton goBack={goBack} setPage={setPage} />

      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-700 via-brand-800 to-brand-900 text-white shadow-hero mb-8">
        <div className="absolute inset-0 bg-hero-sheen pointer-events-none" aria-hidden="true" />
        <div className="relative p-6 sm:p-8 md:p-10">
          <span className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur mb-5" aria-hidden="true">
            <Briefcase className="w-6 h-6 md:w-7 md:h-7" />
          </span>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-[1.15]">Jobs in Education</h1>
          <p className="mt-3 text-[15px] sm:text-base text-white/85 max-w-2xl leading-relaxed">
            Teaching, administrative and support positions across the 14 parishes. Schools and
            education employers can post a vacancy free.
          </p>
          <button
            onClick={startPost}
            className="mt-6 inline-flex items-center gap-2 px-5 min-h-[44px] rounded-2xl font-semibold text-brand-900 bg-white shadow-sm transition-all duration-200 hover:bg-brand-50 hover:shadow-lg active:scale-[0.98]"
          >
            {isAuthenticated ? <Plus className="w-5 h-5" /> : <LogIn className="w-5 h-5" />}
            {isAuthenticated ? 'Post a position' : 'Sign in to post a position'}
          </button>
        </div>
      </section>

      {notice && (
        <div className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-brand-50 ring-1 ring-brand-100 text-[15px] text-brand-900">
          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="flex-1">{notice}</p>
          <button onClick={() => setNotice('')} aria-label="Dismiss" className="p-1 rounded-lg hover:bg-brand-100">
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* ---- Post / edit form ---- */}
      {showForm && (
        <section className="mb-8 bg-canvas-raised rounded-2xl ring-1 ring-hairline shadow-sm p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-extrabold text-ink">
              {editingId ? 'Edit listing' : 'Post a position'}
            </h2>
            <button
              onClick={() => { setShowForm(false); setEditingId(null); }}
              aria-label="Close the form"
              className="p-2 rounded-lg text-ink-muted hover:bg-canvas"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>

          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field label="Position title" required>
                <input className={inputCls} value={form.title} onChange={set('title')} placeholder="e.g. Mathematics Teacher, Grades 9–11" />
              </Field>
            </div>
            <Field label="School or organisation" required>
              <input className={inputCls} value={form.employer_name} onChange={set('employer_name')} />
            </Field>
            <Field label="Parish">
              <select className={inputCls} value={form.parish} onChange={set('parish')}>
                <option value="">Select a parish</option>
                {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Type of role">
              <select className={inputCls} value={form.role_type} onChange={set('role_type')}>
                {ROLE_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Hours">
              <select className={inputCls} value={form.job_type} onChange={set('job_type')}>
                {JOB_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Salary range" hint="Optional, but listings that state pay get far more applicants.">
                <input className={inputCls} value={form.salary_range} onChange={set('salary_range')} placeholder="e.g. $150,000–$190,000 monthly" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="About the role">
                <textarea rows={4} className={inputCls} value={form.description} onChange={set('description')} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Requirements">
                <textarea rows={3} className={inputCls} value={form.requirements} onChange={set('requirements')} placeholder="Qualifications, experience, registration" />
              </Field>
            </div>

            <div className="sm:col-span-2 pt-1">
              <p className="text-sm font-semibold text-ink mb-3">
                How to apply <span className="font-normal text-ink-muted">— at least one</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Application link">
                  <input type="url" className={inputCls} value={form.apply_url} onChange={set('apply_url')} placeholder="https://" />
                </Field>
                <Field label="E-mail">
                  <input type="email" className={inputCls} value={form.contact_email} onChange={set('contact_email')} />
                </Field>
                <Field label="Telephone">
                  <input type="tel" className={inputCls} value={form.contact_phone} onChange={set('contact_phone')} />
                </Field>
              </div>
            </div>

            {formError && (
              <div className="sm:col-span-2 flex items-start gap-2 p-3 rounded-xl bg-red-50 ring-1 ring-red-200 text-sm text-red-800">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                {formError}
              </div>
            )}

            <div className="sm:col-span-2 flex flex-wrap items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 min-h-[44px] rounded-2xl font-semibold text-white bg-brand-700 shadow-sm transition-all duration-200 hover:bg-brand-800 active:scale-[0.98] disabled:opacity-60"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                {editingId ? 'Save changes' : 'Submit for review'}
              </button>
              <p className="text-sm text-ink-muted">
                Listings are checked by an administrator before they appear, and expire after 60 days.
              </p>
            </div>
          </form>
        </section>
      )}

      {/* ---- The poster's own listings ---- */}
      {isAuthenticated && mine.length > 0 && (
        <section className="mb-8">
          <h2 className="text-xl font-extrabold text-ink mb-1">Your listings</h2>
          <p className="text-[15px] text-ink-muted mb-4">
            Only you and the site administrators can see anything that is not yet live.
          </p>
          <div className="space-y-3">
            {mine.map(job => (
              <JobCard
                key={job.id}
                job={job}
                footer={
                  <div className="mt-4 pt-4 border-t border-hairline">
                    {job.status === 'rejected' && job.moderation_note && (
                      <p className="text-sm text-red-800 bg-red-50 ring-1 ring-red-200 rounded-xl p-3 mb-3">
                        <span className="font-semibold">Reviewer’s note: </span>{job.moderation_note}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => startEdit(job)}
                        className="inline-flex items-center gap-1.5 px-3.5 min-h-[40px] rounded-xl text-sm font-semibold text-brand-700 bg-brand-50 ring-1 ring-brand-100 transition-colors hover:bg-brand-100"
                      >
                        <Pencil className="w-4 h-4" aria-hidden="true" /> Edit
                      </button>
                      <button
                        onClick={() => remove(job)}
                        className="inline-flex items-center gap-1.5 px-3.5 min-h-[40px] rounded-xl text-sm font-semibold text-ink-muted ring-1 ring-hairline transition-colors hover:bg-red-50 hover:text-red-700 hover:ring-red-200"
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" /> Delete
                      </button>
                    </div>
                  </div>
                }
              />
            ))}
          </div>
        </section>
      )}

      {/* ---- The public board ---- */}
      <section>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <h2 className="text-xl font-extrabold text-ink">
            Open positions
            {!loading && <span className="ml-2 text-[15px] font-medium text-ink-muted">{jobsList.length}</span>}
          </h2>
          {hasFilters && (
            <button
              onClick={() => setFilters({ parish: '', role_type: '', job_type: '' })}
              className="text-sm font-semibold text-brand-700 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <select
            aria-label="Filter by parish"
            className={inputCls}
            value={filters.parish}
            onChange={(e) => setFilters(f => ({ ...f, parish: e.target.value }))}
          >
            <option value="">All parishes</option>
            {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            aria-label="Filter by type of role"
            className={inputCls}
            value={filters.role_type}
            onChange={(e) => setFilters(f => ({ ...f, role_type: e.target.value }))}
          >
            <option value="">All roles</option>
            {ROLE_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            aria-label="Filter by hours"
            className={inputCls}
            value={filters.job_type}
            onChange={(e) => setFilters(f => ({ ...f, job_type: e.target.value }))}
          >
            <option value="">Any hours</option>
            {JOB_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mx-auto" />
          </div>
        ) : jobsList.length === 0 ? (
          <div className="text-center py-12 px-6 bg-canvas rounded-2xl ring-1 ring-hairline">
            <Briefcase className="w-12 h-12 text-ink-subtle mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-bold text-ink mb-2">
              {hasFilters ? 'Nothing matches those filters' : 'No open positions right now'}
            </h3>
            <p className="text-[15px] text-ink-muted max-w-md mx-auto">
              {hasFilters
                ? 'Try widening the parish or role.'
                : 'Hiring? Post a vacancy and it will appear here once reviewed.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {jobsList.map(job => <JobCard key={job.id} job={job} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default EducationJobsPage;
